/**
 * Write or edit a reflection as a blog entry: title, a headline under it, a cover image, the text, tags,
 * who may read it, and draft / publish. Used by the Reflections tab and the reflection's own page.
 * A free-standing written reflection publishes with a title and at least 300 characters (the backend's rule);
 * reflections on profile items keep their place (the item) and their text rules.
 * Backend: POST/PATCH /reflections/, POST/DELETE /reflections/<id>/cover/.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { AddPhotoAlternate as CoverIcon, Close as CloseIcon } from '@mui/icons-material';
import useSWR from 'swr';
import { axiosService, fetcher } from 'shared/helpers/axios';
import { useUserContext } from 'shared/contexts/UserContext';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import SlateEditor from './SlateEditor';
import { GuidelinesLink } from './reflectionGuidelines';

const TITLE_MAX = 255;
const SUBTITLE_MAX = 200;
const TAGS_MAX = 5;
const PUBLISH_MIN = 300;

const firstError = (d) => {
  if (!d) return null;
  if (typeof d === 'string') return d;
  const v = d.accept_rules || d.content || d.title || d.tags || d.cover || d.detail || d.error || Object.values(d)[0];
  return Array.isArray(v) ? v[0] : v;
};

/** entry: a timeline / article entry to edit; null for a new free-standing reflection; { item, newItem:
 * { content_type_name, object_id } } for a new reflection on a profile item. */
export default function ReflectionEditor({ open, entry, tagSuggestions = [], onClose, onSaved }) {
  const { showSnackbar } = useSnackbar() || {};
  const editing = Boolean(entry?.id);
  const onItem = Boolean(entry?.item);
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [body, setBody] = useState('');
  const [tags, setTags] = useState([]);
  const [visibility, setVisibility] = useState('connections');
  const [cover, setCover] = useState(null);           // { file, url } new | { url } existing | null
  const [saving, setSaving] = useState(false);
  // the reflection guidelines: agreed once, before the first published reflection
  const { user } = useUserContext() || {};
  const { data: myPage } = useSWR(open && user?.id ? `/reflections/page/?author=${user.id}` : null, fetcher);
  const [agree, setAgree] = useState(false);
  const needsAgreement = myPage ? !myPage.rules_accepted : false;
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setTitle(entry?.raw_title ?? entry?.title ?? '');
    setSubtitle(entry?.subtitle || '');
    setBody(entry?.body || '');
    setTags(entry?.own_tags || []);
    // new reflections: for connections, unless the author opens them to everyone on Otable or the public
    setVisibility(entry?.visibility || 'connections');
    setCover(entry?.cover_url ? { url: entry.cover_url } : null);
    setError('');
  }, [open, entry, onItem]);

  const length = body.trim().length;
  const needsLength = !onItem && (entry?.media_type || 'text') === 'text';
  const titled = onItem || title.trim().length >= 3;      // reflections on profile items never needed a title
  const canPublish = titled && (!needsLength || length >= PUBLISH_MIN);

  const pickCover = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/.test(file.type)) { setError('A cover is a PNG or JPEG image.'); return; }
    setCover({ file, url: URL.createObjectURL(file) });
  };

  const save = async (status) => {
    setSaving(true); setError('');
    const payload = { title: title.trim(), subtitle: subtitle.trim(), content: body, tags, visibility, status,
      ...(agree ? { accept_rules: true } : {}) };
    try {
      const res = editing
        ? await axiosService.patch(`/reflections/${entry.id}/`, payload)
        : await axiosService.post('/reflections/', { ...payload, media_type: 'text', ...(entry?.newItem || {}) });
      const id = res.data.id;
      if (cover?.file) {
        const fd = new FormData(); fd.append('cover', cover.file);
        await axiosService.post(`/reflections/${id}/cover/`, fd);
      } else if (!cover && entry?.cover_url) {
        await axiosService.delete(`/reflections/${id}/cover/`);
      }
      showSnackbar?.(status === 'published' ? 'Reflection published' : 'Draft saved', 'success');
      onSaved?.(id);
    } catch (e) {
      setError(firstError(e.response?.data) || 'The reflection could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{editing ? 'Edit reflection' : 'New reflection'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{String(error)}</Alert>}
          {onItem && (
            <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>On {entry.item.kind.toLowerCase()}{entry.item.label ? `: ${entry.item.label}` : ''}</Typography>
          )}

          {/* cover image */}
          {cover ? (
            <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', aspectRatio: '21 / 9', bgcolor: 'action.hover' }}>
              <Box component="img" src={cover.url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              <Tooltip title="Remove the cover">
                <IconButton onClick={() => setCover(null)} size="small"
                  sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Button component="label" variant="outlined" startIcon={<CoverIcon />} sx={{ alignSelf: 'flex-start' }}>
              Add a cover image
              <input hidden type="file" accept="image/png,image/jpeg" onChange={pickCover} />
            </Button>
          )}

          <TextField label="Title" value={title} required={!onItem} fullWidth autoFocus
            onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
            InputProps={{ sx: { fontSize: 20, fontWeight: 700 } }} />
          <TextField label="Headline" placeholder="A line under the title: your motto for this piece" value={subtitle} fullWidth
            onChange={(e) => setSubtitle(e.target.value.slice(0, SUBTITLE_MAX))} helperText={`${subtitle.length}/${SUBTITLE_MAX}`} />

          {(entry?.media_type || 'text') === 'text' ? (
            <Box>
              <SlateEditor value={body} onChange={(v) => setBody(v || '')} richText height={340}
                placeholder="What happened, what you learned, what comes next…" />
              {needsLength && (
                <Typography sx={{ fontSize: 12, mt: 0.5, color: length >= PUBLISH_MIN ? 'text.secondary' : 'warning.main' }}>
                  {length >= PUBLISH_MIN
                    ? `${length} characters · about ${Math.max(1, Math.round(body.split(/\s+/).length / 200))} min read`
                    : `${length}/${PUBLISH_MIN} characters to publish (a draft can be shorter)`}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
              This is {entry.media_type === 'audio' ? 'an audio' : 'a video'} reflection: its recording stays as it is.
            </Typography>
          )}

          <Autocomplete multiple freeSolo options={tagSuggestions} value={tags}
            onChange={(_e, v) => setTags(v.map((t) => t.trim().slice(0, 40)).filter(Boolean).slice(0, TAGS_MAX))}
            renderTags={(value, getTagProps) => value.map((t, i) => <Chip size="small" label={t} {...getTagProps({ index: i })} />)}
            renderInput={(params) => (
              <TextField {...params} label="Tags" helperText={`Up to ${TAGS_MAX}. Leave empty and Otable tags it from what you write.`} />
            )} />
          <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'action.hover' }}>
            <Typography variant="body2">
              Criticise ideas, not people: no personal attacks, nothing defamatory, nothing confidential, no one else’s
              personal data. <GuidelinesLink>Read the reflection guidelines</GuidelinesLink>
            </Typography>
            {needsAgreement && (
              <FormControlLabel sx={{ mt: 0.5 }}
                control={<Checkbox size="small" checked={agree} onChange={(e) => setAgree(e.target.checked)} />}
                label={<Typography variant="body2">I have read the reflection guidelines and my reflections will follow them.</Typography>} />
            )}
          </Box>
          <TextField select label="Who can read it" value={visibility} onChange={(e) => setVisibility(e.target.value)} sx={{ maxWidth: 360 }}>
            <MenuItem value="connections">My connections</MenuItem>
            <MenuItem value="otable">Everyone on Otable</MenuItem>
            <MenuItem value="public">Anyone, on my public Reflections page</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={() => save('draft')} disabled={saving || !titled}>
          {entry?.status === 'published' ? 'Unpublish (draft)' : 'Save draft'}
        </Button>
        <Button variant="contained" onClick={() => save('published')} disabled={saving || !canPublish || (needsAgreement && !agree)}>
          {saving ? <CircularProgress size={18} /> : entry?.status === 'published' ? 'Update' : 'Publish'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
